import React from "react";
import { Link } from "react-router-dom";
import { Sun, Mail, Phone, MapPin, Facebook, Youtube, Linkedin } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const footerLinks = {
    "Produkty": [
        { label: "FVE Panely", to: "/kategorie/fve-panely" },
        { label: "Střídače", to: "/kategorie/stridace" },
        { label: "Baterie", to: "/kategorie/baterie" },
        { label: "Loxone Smart Home", to: "/loxone" },
        { label: "Nabíjecí stanice", to: "/kategorie/nabijeci-stanice" },
        { label: "Carporty", to: "/kategorie/carporty" },
    ],
    "Služby": [
        { label: "Instalace FVE", to: "/sluzby" },
        { label: "Smart Home (Loxone)", to: "/sluzby" },
        { label: "Revize a servis", to: "/stranka/servis" },
        { label: "FVE konfigurátor", to: "/fve" },
        { label: "Poradenství", to: "/sluzby" },
    ],
    "Firma": [
        { label: "O nás", to: "/stranka/kontakt" },
        { label: "Blog", to: "/blog" },
        { label: "Dotace", to: "/stranka/dotace" },
        { label: "Kontakt", to: "/stranka/kontakt" },
    ],
    "Podpora": [
        { label: "Obchodní podmínky", to: "/stranka/obchodni-podminky" },
        { label: "Reklamační řád", to: "/stranka/reklamacni-rad" },
        { label: "Odstoupení od smlouvy", to: "/stranka/odstoupeni-od-smlouvy" },
        { label: "Ochrana osobních údajů", to: "/stranka/ochrana-osobnich-udaju" },
        { label: "Doprava a platba", to: "/stranka/doprava-platba" },
    ],
};

const brands = ["GoodWe", "SolaX", "Dyness", "LONGi", "Aiko", "Loxone", "Helukabel", "TIGO"];

export default function Footer() {
    const { theme } = useTheme();

    return (
        <footer className="bg-gray-950">
            <div className="sg-container py-14">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
                    {/* Brand */}
                    <div className="col-span-2">
                        <Link to="/" className="flex items-center gap-2.5 mb-5">
                            {theme.logo_url ? (
                                <img src={theme.logo_url} alt="Solar-Green" className="h-10 w-auto brightness-0 invert" style={{ maxWidth: "180px" }} />
                            ) : (
                                <>
                                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-lime-400 rounded-xl flex items-center justify-center shadow-lg shadow-green-900/30">
                                        <Sun size={20} className="text-white" />
                                    </div>
                                    <div>
                                        <span className="text-lg font-bold text-white">SOLAR</span>
                                        <span className="text-lg font-bold text-lime-400">-GREEN</span>
                                    </div>
                                </>
                            )}
                        </Link>
                        <p className="text-sm text-gray-400 leading-relaxed mb-5 max-w-xs">
                            {theme.company_name || "SOLAR GREEN s.r.o."} — Specialisté na fotovoltaiku, smart home Loxone a technologické instalace na klíč.
                        </p>
                        <div className="space-y-2.5 text-sm">
                            <a href={`tel:${theme.phone}`} className="flex items-center gap-2.5 text-gray-400 hover:text-green-400 transition-colors">
                                <Phone size={14} /> {theme.phone}
                            </a>
                            <a href={`mailto:${theme.email}`} className="flex items-center gap-2.5 text-gray-400 hover:text-green-400 transition-colors">
                                <Mail size={14} /> {theme.email}
                            </a>
                            {theme.office_address && (
                                <div className="flex items-center gap-2.5 text-gray-500">
                                    <MapPin size={14} /> Office: {theme.office_address}
                                </div>
                            )}
                            {theme.billing_address && (
                                <div className="flex items-center gap-2.5 text-gray-500">
                                    <MapPin size={14} /> Fakturace: {theme.billing_address}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 mt-5">
                            {[Facebook, Youtube, Linkedin].map((Icon, i) => (
                                <a key={i} href="#" className="w-9 h-9 bg-gray-800 rounded-xl flex items-center justify-center text-gray-400 hover:bg-green-600 hover:text-white transition-all duration-200">
                                    <Icon size={15} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Links */}
                    {Object.entries(footerLinks).map(([title, links]) => (
                        <div key={title}>
                            <h4 className="font-semibold text-white mb-4 text-sm">{title}</h4>
                            <ul className="space-y-2.5">
                                {links.map(link => (
                                    <li key={link.label}>
                                        <Link to={link.to} className="text-sm text-gray-400 hover:text-green-400 transition-colors">
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Brands */}
                <div className="mt-10 pt-8 border-t border-gray-800/60">
                    <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-3 font-medium">Značky které prodáváme</p>
                    <div className="flex flex-wrap gap-2">
                        {brands.map(brand => (
                            <span key={brand} className="px-3 py-1 bg-gray-900 rounded-full text-xs text-gray-500 border border-gray-800">
                                {brand}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom */}
            <div className="border-t border-gray-800/60">
                <div className="sg-container py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                    <span>&copy; {new Date().getFullYear()} {theme.company_name || "SOLAR GREEN s.r.o."} - Všechna práva vyhrazena</span>
                    <div className="flex items-center gap-3">
                        {theme.ico && <span>IČ: {theme.ico}</span>}
                        {theme.ico && theme.dic && <span className="text-gray-800">|</span>}
                        {theme.dic && <span>DIČ: {theme.dic}</span>}
                        <span className="text-gray-800">|</span>
                        <Link to="/stranka/ochrana-osobnich-udaju" className="hover:text-gray-400 transition-colors">Ochrana osobních údajů</Link>
                        <span className="text-gray-800">|</span>
                        <Link to="/stranka/obchodni-podminky" className="hover:text-gray-400 transition-colors">Obchodní podmínky</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
